import { FC, useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

interface BeforeAfterSliderProps {
  beforeImage: string;
  afterImage: string;
  beforeAlt: string;
  afterAlt: string;
  title: string;
  description: string;
  index: number;
  location?: string;
  completionDate?: string;
}

const BeforeAfterSlider: FC<BeforeAfterSliderProps> = ({
  beforeImage,
  afterImage,
  beforeAlt,
  afterAlt,
  title,
  description,
  index,
  location,
  completionDate
}) => {
  const [position, setPosition] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const handleMove = (clientX: number) => {
    if (!containerRef.current) return;
    
    const container = containerRef.current;
    const rect = container.getBoundingClientRect();
    const x = clientX - rect.left;
    const containerWidth = rect.width;
    
    // Calculate position as percentage
    let newPosition = (x / containerWidth) * 100;
    
    // Clamp position between 0 and 100
    newPosition = Math.max(0, Math.min(100, newPosition));
    
    setPosition(newPosition);
  };

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault(); // Prevent default selection behavior
    setIsDragging(true);
    
    // For immediate position update on click (not just drag)
    if ('clientX' in e) {
      handleMove(e.clientX);
    } else if (e.touches && e.touches[0]) {
      handleMove(e.touches[0].clientX);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      e.preventDefault(); // Prevent text selection while dragging
      handleMove(e.clientX);
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (isDragging && e.touches.length > 0) {
      handleMove(e.touches[0].clientX);
    }
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('touchmove', handleTouchMove, { passive: false });
      window.addEventListener('touchend', handleMouseUp);
      
      // Disable text selection on the body while dragging
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleMouseUp);
      
      // Re-enable text selection
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
    };
  }, [isDragging]);

  return (
    <motion.div 
      className="bg-white rounded-lg shadow-lg overflow-hidden"
      initial={{ opacity: 0, y: 50 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.2 }}
      ref={ref}
    >
      <div className="relative">
        <div 
          ref={containerRef}
          className="h-80 md:h-96 w-full bg-gray-200 relative select-none"
          style={{ touchAction: 'none' }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleMouseDown}
        >
          {/* Container for both images with identical positioning */}
          <div className="absolute inset-0">
            {/* After image (always full width) */}
            <img 
              src={afterImage} 
              alt={afterAlt || `${title} po transformacji - Aluxim`}
              className="absolute inset-0 w-full h-full object-cover select-none"
              draggable="false"
              style={{ pointerEvents: 'none' }}
              loading="lazy" // Add lazy loading for better performance
              width="800" // Add dimensions for better CLS performance
              height="600"
            />
            
            {/* Before image with clip path */}
            <img 
              src={beforeImage} 
              alt={beforeAlt || `${title} przed transformacją - Aluxim`}
              className="absolute inset-0 w-full h-full object-cover select-none"
              draggable="false"
              style={{ 
                pointerEvents: 'none',
                clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)` 
              }}
              loading="lazy" // Add lazy loading for better performance
              width="800" // Add dimensions for better CLS performance
              height="600"
            />
          </div>
          
          {/* Slider divider line with enhanced visibility */}
          <div 
            className="absolute top-0 bottom-0 w-0.5 bg-white cursor-ew-resize z-10"
            style={{ 
              left: `${position}%`,
              boxShadow: '0 0 8px rgba(0, 0, 0, 0.6)' 
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleMouseDown}
          >
            {/* Handle for the slider */}
            <div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center z-20"
              style={{ boxShadow: '0 0 10px rgba(0, 0, 0, 0.5)' }}
            >
              {/* Simple horizontal arrow design */}
              <svg className="w-5 h-5 text-blue-700" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9 8L5 12L9 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 8L19 12L15 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {/* BEFORE/AFTER label with improved contrast */}
          <div className="absolute top-4 left-0 z-10">
            <div className="bg-blue-700 text-white text-xs px-3 py-1 rounded-r-full shadow-md">
              PRZED i PO
            </div>
          </div>
          
          {/* Added before/after text indicators for clarity */}
          <div className="absolute bottom-4 left-4 text-white font-bold text-sm z-10 bg-black/50 px-2 py-1 rounded">
            PRZED
          </div>
          <div className="absolute bottom-4 right-4 text-white font-bold text-sm z-10 bg-black/50 px-2 py-1 rounded">
            PO
          </div>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-2">{title}</h3>
        <p className="text-gray-600 mb-3">{description}</p>
        {(location || completionDate) && (
          <div className="flex flex-wrap text-sm text-gray-500 mt-2">
            {location && (
              <div className="flex items-center mr-4 mb-1">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path>
                </svg>
                {location}
              </div>
            )}
            {completionDate && (
              <div className="flex items-center mb-1">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
                {new Date(completionDate).toLocaleDateString('pl-PL')}
              </div>
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default BeforeAfterSlider;
